import { useCallback, useState, useEffect } from "react";

export const useLocalStorage = (key: string, defaultValue: string) => {
    const readValue = useCallback(() => {
        try {
            const raw = window.localStorage.getItem(key);
            return raw ? raw : defaultValue;
        } catch (error) {
            console.warn(`Error reading localStorage key “${key}”:`, error);
            return defaultValue;
        }
    }, [key, defaultValue]);

    const [storedValue, setStoredValue] = useState(() => readValue());

    // Re-read value when key changes
    useEffect(() => {
        setStoredValue(readValue());
    }, [key, readValue]);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key === key) {
                setStoredValue(event.newValue ?? defaultValue);
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => {
            window.removeEventListener("storage", handleStorage);
        };
    }, [key, defaultValue]);

    const setValue = useCallback(
        (newValue?: string) => {
            try {
                if (newValue === undefined) {
                    window.localStorage.removeItem(key);
                    setStoredValue(defaultValue);
                } else {
                    window.localStorage.setItem(key, newValue);
                    setStoredValue(newValue);
                }
                // Trigger a storage event
                window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
            } catch (error) {
                console.warn(`Error setting localStorage key “${key}”:`, error);
            }
        },
        [key, defaultValue],
    );

    return { storedValue, setValue };
};
